import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { List, X, ArrowUpRight } from "@phosphor-icons/react";
import { NAV_LINKS } from "../data";

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 80);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const navSolid = scrolled || !isHome;

  return (
    <div className="min-h-screen bg-bone text-olive-deep">
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          navSolid
            ? "bg-bone/95 backdrop-blur-md border-bottom border-olive/10 h-20 shadow-sm"
            : "bg-transparent h-24"
        }`}
        data-testid="site-header"
      >
        {/* Subtle top gradient for readability when transparent */}
        {!navSolid && (
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 to-transparent pointer-events-none" />
        )}

        <div className="max-w-8xl mx-auto px-6 lg:px-12 h-full relative z-10">
          <div className="flex items-center justify-between h-full">
            <Link
              to="/"
              className="flex items-baseline gap-2"
              data-testid="brand-logo-link"
            >
              <span
                className={`font-heading text-2xl transition-colors ${
                  navSolid ? "text-olive-deep font-medium" : "text-white font-bold"
                }`}
              >
                Axon
              </span>
              <span
                className={`font-heading text-2xl font-light italic transition-colors ${
                  navSolid ? "text-terracotta" : "text-white"
                }`}
              >
                Premium
              </span>
              <span
                className={`overline ml-2 hidden sm:inline transition-colors ${
                  navSolid ? "text-terracotta" : "text-white/70"
                }`}
              >
                Agri · Dairy
              </span>
            </Link>

            <nav
              className="hidden lg:flex items-center gap-9"
              data-testid="primary-nav"
            >
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`nav-link-${l.label
                    .toLowerCase()
                    .replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `text-sm tracking-wide transition-colors ${
                      isActive
                        ? "text-terracotta"
                        : navSolid
                        ? "text-olive hover:text-olive-deep"
                        : "text-white/90 hover:text-white"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </nav>

            <div className="flex items-center gap-2 sm:gap-3">
              <Link
                to="/contact"
                className="hidden lg:flex items-center justify-center gap-2 transition-all duration-300 font-medium uppercase px-4 py-2 text-[11px] tracking-widest lg:px-6 lg:py-3 lg:text-[0.78rem] lg:tracking-normal rounded-sm bg-[#C9A84C] text-[#1A1A1A] hover:bg-[#AB8B38] transition-colors shadow-lg"
                style={{
                  backgroundColor: "#C9A84C",
                  color: "#1A1A1A",
                }}
                data-testid="header-cta-button"
              >
                Partner With Us
                <ArrowUpRight size={14} weight="bold" />
              </Link>
              <button
                onClick={() => setOpen(!open)}
                className={`lg:hidden p-2 -mr-2 transition-colors ${
                  navSolid ? "text-olive-deep" : "text-white"
                }`}
                aria-label="Toggle menu"
                data-testid="mobile-menu-toggle"
              >
                {open ? <X size={22} /> : <List size={22} />}
              </button>
            </div>
          </div>
        </div>

        {open && (
          <div
            className="lg:hidden border-t border-olive/10 bg-bone h-screen"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-12 flex flex-col gap-8">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`mobile-nav-link-${l.label
                    .toLowerCase()
                    .replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `text-3xl font-heading tracking-wide ${
                      isActive ? "text-terracotta" : "text-olive-deep"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                className="btn-primary mt-4 self-start"
                data-testid="mobile-header-cta-button"
              >
                Partner With Us
                <ArrowUpRight size={14} weight="bold" />
              </Link>
            </div>
          </div>
        )}
      </header>

      <main>{children}</main>

      <Footer />
    </div>
  );
}

function Footer() {
  return (
    <footer
      className="bg-olive text-bone mt-32"
      data-testid="site-footer"
    >
      <div className="max-w-8xl mx-auto px-6 lg:px-12 py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="flex items-baseline gap-2">
              <span className="font-heading text-3xl text-bone">Axon</span>
              <span className="font-heading text-3xl italic text-terracotta">
                Premium
              </span>
            </div>
            <p className="mt-6 text-bone/70 max-w-md leading-relaxed">
              From our soil. From our cattle. For your business. A vertically
              integrated, farm-to-product premium dairy enterprise built for
              institutional buyers and global distribution.
            </p>
            <div className="mt-8 flex flex-wrap gap-2">
              {["FSSAI", "ISO 22000", "AGMARK", "APEDA"].map((c) => (
                <span
                  key={c}
                  className="text-xs font-mono tracking-widest2 uppercase border border-bone/25 px-2.5 py-1 text-bone/80"
                  data-testid={`footer-cert-${c.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {c}
                </span>
              ))}
            </div>
          </div>

          <div className="md:col-span-3">
            <h4 className="overline text-bone/60 mb-5">Navigate</h4>
            <ul className="space-y-3 text-sm">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="footer-link text-bone/85"
                    data-testid={`footer-link-${l.label
                      .toLowerCase()
                      .replace(/\s+/g, "-")}`}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="overline text-bone/60 mb-5">Contact</h4>
            <ul className="space-y-3 text-sm text-bone/85">
              <li>
                <span className="block text-bone/55 text-xs uppercase tracking-widest2 font-mono mb-1">
                  HQ
                </span>
                AxonPremium Agribusiness Pvt. Ltd.
                <br />
                6/117 B street no vishwas nagar delhi 110032
              </li>
              <li>
                <span className="block text-bone/55 text-xs uppercase tracking-widest2 font-mono mb-1">
                  Trade & Export
                </span>
                Axonpremiumpvtltd@gmail.com
                <br />
                +91 88003 95365
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-bone/15 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs font-mono uppercase tracking-widest2 text-bone/55">
          <span>© {new Date().getFullYear()} AxonPremium · All rights reserved</span>
          <span>Vertically integrated · Pharmaceutical-grade · Export-ready</span>
        </div>
      </div>
    </footer>
  );
}
